import React, { useEffect, useState } from "react";
import {
  RushOrderColors,
  RushOrderLabels,
  RushOrders as RushOrdersType,
  RushOrderType,
} from "../../types";
import { HiRefresh } from "react-icons/hi";
import { useFetchRushOrdersQuery } from "../../store";
import Button from "./Button";
import Skeleton from "./Skeleton";
import RushOrdersList from "./RushOrdersList";

const AUTO_REFRESH_INTERVAL = 60_000; // 60 seconds

const RushOrders: React.FC = () => {
  const { data, isFetching, refetch } = useFetchRushOrdersQuery();
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedRushOrderType, setSelectedRushOrderType] = useState<
    RushOrderType | undefined
  >();

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refetch]);

  // Update "Dernière mise à jour" when data changes
  useEffect(() => {
    if (data) setLastUpdated(new Date());
  }, [data]);

  const rushData: RushOrdersType | undefined = data;

  return (
    <div className="w-full p-4 border-b-2 border-gray-600">
      <h2 className="text-xl font-semibold">Commandes urgentes</h2>

      <p className="text-sm text-gray-500 mb-4">
        {lastUpdated
          ? `Dernière mise à jour : ${lastUpdated.toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : "En attente de données..."}
      </p>

      <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200">
        {rushData && (
          <table className="min-w-full text-center border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 border-r font-medium sticky left-0 bg-gray-100">
                  Action
                </th>
                {(Object.keys(rushData) as (keyof RushOrdersType)[]).map(
                  (key) => (
                    <th
                      key={key}
                      className="p-3 border-r font-medium text-sm"
                    >
                      {RushOrderLabels[key]}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {rushData ? (
                <tr>
                  <td className="p-3 border-r sticky left-0 bg-white">
                    <Button
                      onClick={() => refetch()}
                      size="sm"
                      variant="secondary"
                      disabled={isFetching}
                      className="flex items-center gap-1"
                    >
                      <HiRefresh
                        className={`h-4 w-4 ${
                          isFetching ? "animate-spin" : ""
                        }`}
                      />
                      {isFetching ? "Chargement..." : "Actualiser"}
                    </Button>
                  </td>
                  {(Object.keys(rushData) as (keyof RushOrdersType)[]).map(
                    (key) => (
                      <td
                        key={key}
                        className={`p-3 text-white font-semibold cursor-pointer ${
                          RushOrderColors[key]
                        } ${
                          key === selectedRushOrderType
                            ? "border-4 border-black"
                            : ""
                        }`}
                        onClick={() => {
                          if (selectedRushOrderType === key) {
                            setSelectedRushOrderType(undefined);
                          } else if (key !== RushOrderType.PriorityCount) {
                            setSelectedRushOrderType(key as RushOrderType);
                          }
                        }}
                      >
                        {rushData[key]}
                      </td>
                    )
                  )}
                </tr>
              ) : (
                <tr>
                  <td
                    colSpan={Object.keys(rushData).length + 1}
                    className="p-4"
                  >
                    {/* ✅ Use Skeleton instead of plain text */}
                    <Skeleton
                      times={1}
                      className="h-8 w-full"
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selectedRushOrderType && (
        <RushOrdersList rushOrderType={selectedRushOrderType} />
      )}
    </div>
  );
};

export default RushOrders;
